import {Component, Inject, OnInit} from '@angular/core';
import {ClaimService} from '../../../services/claim.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {WorkflowResultDTO} from '../../../models/WorkflowResultDTO';
import {SharedService} from '../../../../common/services/shared-service';
import {KeycloakService} from 'keycloak-angular';
import {FormControl, Validators} from '@angular/forms';
import {PaymentDTO} from '../../../models/payment/PaymentDTO';
import {PaymentsService} from '../../../services/payments.service';
import {finalize, take} from 'rxjs/operators';
import {TransactionPaymentLinkDTO} from '../../../models/payment/TransactionPaymentLinkDTO';
import {ProcessingStatus} from '../../../types/processing-status.enum';
import {CreateNameValueDTO} from '../../../../common/models/NameValueDTO';

@Component({
  selector: 'app-add-manual-payment-dialog',
  templateUrl: './add-manual-payment-dialog.component.html',
  styleUrls: [
    '../../../../../../styles/global/_dialog-styles.scss'
    ]
})
export class AddManualPaymentDialogComponent implements OnInit {

  transactionId: number;
  paymentId: number;
  adjudicationStatus: string;
  paymentDTO: PaymentDTO;

  isEdit: boolean;
  userFirstLastName: string;
  heading: string;
  isBusyCounterInlineSpinner = 0;
  paymentReferenceFormControl: FormControl;
  completionDateFormControl: FormControl;

  constructor(
    private  claimService: ClaimService,
    private sharedService: SharedService,
    private keycloakService: KeycloakService,
    private paymentService: PaymentsService,
    public dialogRef: MatDialogRef<AddManualPaymentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { transactionId: number, paymentId: number, adjudicationStatus: string}
  ) {
    this.isEdit = data.paymentId != null;
    this.paymentId = data.paymentId;
    this.transactionId = data.transactionId;
    this.adjudicationStatus = data.adjudicationStatus;

    this.heading = this.isEdit ? 'Edit Manual Payment' : 'Add Manual Payment';
    this.paymentDTO = new PaymentDTO();

    this.paymentReferenceFormControl = new FormControl('', Validators.required);
    this.completionDateFormControl = new FormControl(new Date(), Validators.required);
  }

  ngOnInit(): void {
    if (this.isEdit) {
      this.setFormsForEdit();
    }
    this.keycloakService.loadUserProfile().then(profile => {
      this.userFirstLastName = `${profile.firstName} ${profile.lastName}`;
    });
    this.sharedService.getHelpInfo('Payment');
  }

  getInfoTooltip(inputName: string) {
    return this.sharedService.getInfoTooltip(inputName);
  }

  onSaveClick() {
    this.isBusyCounterInlineSpinner++;
    this.paymentDTO = this.createPaymentDTO();
    this.paymentService.savePayment(this.paymentDTO)
      .pipe(
        take(1),
        finalize(() => this.isBusyCounterInlineSpinner--)
      )
      .subscribe(
      () => {
        this.markClaimAsPaid();
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
      }
    );
  }

  onCancelClick() {
    this.dialogRef.close(false);
  }

  disableSaveButton(): boolean {
    return this.paymentReferenceFormControl.invalid || !this.paymentReferenceFormControl.value || this.paymentReferenceFormControl.value === '' || this.completionDateFormControl.invalid;
  }

  private markClaimAsPaid() {
    const workflowResultDTO = this.createWorkflowResultDTO();
    this.claimService.markClaimAsPaid(workflowResultDTO)
      .pipe(
        take(1)
      )
      .subscribe(
      () => {
        this.sharedService.openSnackBar(`Claim ${this.transactionId} successfully marked as paid`, 'Dismiss');
        this.dialogRef.close(true);
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
      }
    );
  }

  private createWorkflowResultDTO(): WorkflowResultDTO {
    const workflowResultDTO = new WorkflowResultDTO();
    workflowResultDTO.transactionId = this.transactionId;
    workflowResultDTO.workflowStep = ProcessingStatus.Paid;
    workflowResultDTO.result = this.adjudicationStatus;
    workflowResultDTO.isManualIntervention = true;
    workflowResultDTO.createdBy = this.userFirstLastName;
    return workflowResultDTO;
  }

  private createPaymentDTO(): PaymentDTO {
    const payment: PaymentDTO = this.isEdit ? this.paymentDTO : new PaymentDTO();
    payment.paymentReference = this.paymentReferenceFormControl.value;
    payment.bankAccountNumber = 'Unknown';
    payment.isActive = 2; // manual flag
    payment.isImmediate = 0;
    payment.paymentExecutionDate = new Date(this.completionDateFormControl.value);
    payment.paymentCompletionDate = new Date(this.completionDateFormControl.value);
    payment.paymentType = CreateNameValueDTO(5, 'Manual Payment'); // 5 matches DB side
    if (this.isEdit) {
      payment.updatedBy = this.userFirstLastName;
      payment.updated = new Date();
    } else {
      payment.createdBy = this.userFirstLastName;
      payment.created = new Date();
    }

    const transactionPayments: TransactionPaymentLinkDTO[] = [];
    const transactionPayment = new TransactionPaymentLinkDTO();
    transactionPayment.transactionId = this.transactionId;
    transactionPayment.createdBy = payment.createdBy;
    transactionPayment.created = payment.created;
    transactionPayment.updatedBy = payment.updatedBy;
    transactionPayment.updated = payment.updated;
    transactionPayments.push(transactionPayment);
    payment.transactionPayments = transactionPayments;

    return payment;
  }

  private setFormsForEdit() {
    this.paymentService.getPayment(this.paymentId).subscribe(
      (paymentDTO) => {
        this.paymentDTO = paymentDTO;
        this.paymentReferenceFormControl.setValue(this.paymentDTO.paymentReference);
        this.completionDateFormControl.setValue(new Date(this.paymentDTO.paymentCompletionDate));
      }
    );
  }

}
